import { bkgrAudio } from '../zustand';
import { IDataActions, ITrackingImageDict, useDataStore } from '../zustand/data-store';
import { ISceneActions, ITransCatDict, ITransCatTypes, useSceneStore } from '../zustand/scene-store';
import { IContentDoc, IRoot, ISceneComp, ITrackingContentDocByTrackingType } from '../components/r3f-components/component-data-structure';
import { useAssetPreloader as preloadAssets } from './useAssetPreloader';
import { TransitionManager } from '../components/r3f-components/transitions/transitionManager';
import { Jute } from '..';
import { filestore, FsAudio, FsImageTarget } from '@/filestore';
import { isSafariWithAudioContextBug } from '@/components/r3f-components/utils/general';
import { addMediaElementToAudioStreamDestination } from '@/components/r3f-components/utils/audio-manager';

export const transitionManager: { [sceneId: string]: TransitionManager<any> } = {};

let contentLoaderCalled = false;

export const useContentLoader = (contentData: IContentDoc): void => {
	const dataDispatch = useDataStore((state) => state.dispatch);
	const sceneDispatch = useSceneStore((state) => state.dispatch);
	if (contentLoaderCalled) return;
	contentLoaderCalled = true;

	async function setupContentState() {
		const { componentsById, rootComponentId, tracking, webEmbed, shadowsEnabled = true, device = null, screenRelativeScaleType = 'height-relative' } = contentData;

		// Update zustand with new state
		const root = componentsById[rootComponentId] as IRoot;
		let transCatDict: ITransCatDict = {};
		// set up initial zustand state with all scenes hidden
		root.children.forEach((sceneId, i) => {
			// if in test or non ar mode, don't await tracking target to become visible and show first scene immediately and
			transCatDict[sceneId] = Jute.isIframe ? (i === 0 ? ITransCatTypes.enter : ITransCatTypes.hidden) : ITransCatTypes.hidden;

			//transCatDict[sceneId] = ITransCatTypes.hidden;
			transitionManager[sceneId] = new TransitionManager();

			// Add to scene id/name mapping
			Jute.sceneTitleById[sceneId] = (componentsById[sceneId] as ISceneComp).title;
		});
		dataDispatch({
			type: IDataActions.ADD_CONTENT,
			componentsById,
			webEmbed,
			shadowsEnabled,
			webXR: device,
			screenRelativeScaleType
		});
		
		dataDispatch({
			type: IDataActions.ADD_ROOT_ID,
			rootId: rootComponentId,
		});

		if (typeof root?.bkgrSound?.filestoreId === 'string') {
			bkgrAudio.loop = !!root?.bkgrSound?.loop;
			bkgrAudio.crossOrigin = 'anonymous';
			const audioFile = filestore.get<FsAudio>(root.bkgrSound.filestoreId);
			bkgrAudio.src = audioFile.getMp3Url()
			if (!isSafariWithAudioContextBug()) {
				const isPaused = bkgrAudio.paused;
				addMediaElementToAudioStreamDestination(Jute.audioContext, Jute.audioStreamDestination, bkgrAudio);
				if (isPaused) {
					bkgrAudio.pause();
					bkgrAudio.currentTime = 0;
				}
			}
		}

		sceneDispatch?.({
			type: ISceneActions.SET_ACTIVE_SCENE_ID,
			activeSceneId: (componentsById[rootComponentId] as IRoot).children[0],
		});
		sceneDispatch?.({
			type: ISceneActions.SET_TRANSITION_CATEGORY,
			transCatDict,
		});
		return {
			componentsById,
			rootId: rootComponentId,
			tracking,
		};
	}

	async function getImageTrackingData(tracking: ITrackingContentDocByTrackingType | undefined) {
		// Fetch tracking data
		if (tracking && tracking['image']) {
			const trkDict = tracking['image'];
			const ids = Object.keys(trkDict);
			const trackingImageDict: ITrackingImageDict = {}

			for (let i = 0; i < ids.length; i++) {
				const trackingImageData = trkDict[ids[i]];
				if (!trackingImageData.filestoreId) {
					continue;
				};
				const fsFile = filestore.get<FsImageTarget>(trackingImageData.filestoreId);
				const trackingImageFile = await fsFile.getZpt();
				const trackingImageUrl = fsFile.getImageUrl();
				if (!trackingImageFile) {
					continue;
				};
				trackingImageDict[trackingImageData.id] = {...trackingImageData, file: trackingImageFile, imageUrl: trackingImageUrl};
			}
			dataDispatch({ type: IDataActions.ADD_CONTENT, trackingImageDict });
		}
	}

	async function loadContent() {
		Jute.loading?.();
		const { componentsById, rootId, tracking } = await setupContentState();
		return Promise.all([getImageTrackingData(tracking), preloadAssets({ componentsById, rootId })]);
	}

	loadContent().then(() => {
		Jute.loaded?.();
		console.log('assets loaded')
	});
};